import ManageMySpending from "./ManageMySpending";
import { Button1, Button2 } from "../buttons/Buttons";
import ProgressBar from "../progress-bar/ProgressBar";
import Select from "../select/Select";
import MultiColorBar from "../multi-color-bar/MultiColorBar";
import Checkbox from "../checkbox/Checkbox";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

import settingIcon from "../../assets/icons/settings-icon-green.png";
import { DatePicker, Input, Modal, Spin } from "antd";
import { useMemo, useState } from "react";
import rentIcon from "../../assets/images/rent.png";
import loanIcon from "../../assets/images/loan-icon.png";
import gameIcon from "../../assets/images/game.png";
import cartIcon from "../../assets/images/cart.png";
import cardIcon from "../../assets/images/credit-card-icon.png";
import healthIcon from "../../assets/images/health.png";
import travelIcon from "../../assets/images/travel.png";
import giftIcon from "../../assets/images/gift.png";
import Table from "../table/Table";
import plusIcon from "../../assets/icons/plus-icon.png";
import downIcon from "../../assets/icons/chevron-down.png";
import circleIcon from "../../assets/icons/Ellipse 81.png";
import deleteIcon from "../../assets/icons/trashcan-icon.png";
import { useTransactions, useAccounts } from "../../data/live";
import { useNavigate } from "react-router-dom";

/** Map a category name to a known icon; fall back to card icon */
const CATEGORY_ICON_MAP = {
  Rent: rentIcon,
  "Mortgage & Loans": loanIcon,
  Groceries: cartIcon,
  "Others & ATM": cardIcon,
  Health: healthIcon,
  "Gift & Donation": giftIcon,
  Travel: travelIcon,
  "Hobbies & Entertainment": gameIcon,
};
const CATEGORY_COLOR_MAP = {
  Rent: "#E8550D",
  "Mortgage & Loans": "#F7A64F",
  Groceries: "#F2E103",
  "Others & ATM": "#1BBB6B",
  Health: "#3CADF1",
  "Gift & Donation": "#2660CE",
  Travel: "#922590",
  "Hobbies & Entertainment": "#C9C9C9",
};
const fallbackColor = (cat) => {
  let h = 0;
  for (let i = 0; i < (cat || "").length; i++) h = (h * 31 + cat.charCodeAt(i)) | 0;
  const palette = ["#E8550D", "#F7A64F", "#F2E103", "#1BBB6B", "#3CADF1", "#2660CE", "#922590", "#C9C9C9"];
  return palette[Math.abs(h) % palette.length];
};

const fmt = (n, ccy = "AUD") =>
  `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${ccy}`;

const SpendingReport = () => {
  const [openModal, setOpenModal] = useState(false);
  const [addTag, setAddTag] = useState([1]);
  const [daysFilter, setDaysFilter] = useState(30);
  const [typeFilter, setTypeFilter] = useState("all");
  const navigate = useNavigate();

  const { items: transactions, loading: txLoading } = useTransactions();
  const { accounts, loading: accLoading } = useAccounts();

  /** Filter transactions to last N days */
  const recentTx = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysFilter);
    return transactions.filter((t) => {
      const d = new Date(t.raw?.createdAt || t.date);
      const inRange = d >= cutoff;
      const matchesType = typeFilter === "all" || t.direction === typeFilter;
      return inRange && matchesType;
    });
  }, [transactions, daysFilter, typeFilter]);

  /** Total income and expenses */
  const totalIncome = useMemo(
    () => recentTx.filter((t) => t.direction === "in").reduce((s, t) => s + Number(t.amount || 0), 0),
    [recentTx]
  );
  const totalSpending = useMemo(
    () => recentTx.filter((t) => t.direction === "out").reduce((s, t) => s + Number(t.amount || 0), 0),
    [recentTx]
  );

  /** Bucket expenses by category */
  const categoryBreakdown = useMemo(() => {
    const map = {};
    for (const t of recentTx) {
      if (t.direction !== "out") continue;
      const cat = t.category || "Others & ATM";
      map[cat] = (map[cat] || 0) + Number(t.amount || 0);
    }
    // Sort descending
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .map(([cat, amount]) => ({
        categoryData: {
          icon: CATEGORY_ICON_MAP[cat] || cardIcon,
          color: CATEGORY_COLOR_MAP[cat] || fallbackColor(cat),
          name: cat,
        },
        percentage:
          totalSpending > 0 ? Math.round((amount / totalSpending) * 1000) / 10 : 0,
        amount: `-${fmt(amount)}`,
      }));
  }, [recentTx, totalSpending]);

  /** Add a "Total" row */
  const spendingReportData = useMemo(
    () => [
      ...categoryBreakdown,
      {
        categoryData: { name: "Total" },
        amount: `-${fmt(totalSpending)}`,
      },
    ],
    [categoryBreakdown, totalSpending]
  );

  /** Multi-color bar segments */
  const multiColorSegments = useMemo(
    () => categoryBreakdown.map((c) => c.categoryData.color),
    [categoryBreakdown]
  );

  /** Real accounts for the filter sidebar */
  const accountsList = useMemo(
    () =>
      accounts.map((a) => ({
        icon: circleIcon,
        name: `${a.currency} - ${a.accountName || a.type || "Account"}`,
        id: a.id,
      })),
    [accounts]
  );

  const spendingReportHeaders = ["Categories", "Percentage(%)", "Amount"];

  if (txLoading && accLoading) {
    return (
      <ManageMySpending
        navName={"Spending Report"}
        showQuickAction={true}
        pageClassName="dashboard-page report-theme-page spending-report-page"
        contentClassName="dashboard-content"
      >
        <div className="d-flex justify-content-center py-5">
          <Spin size="large" />
        </div>
      </ManageMySpending>
    );
  }

  return (
    <>
      {/* Modal For Manage Tags */}
      <Modal
        title={<p className="text-center fs-5 fw-semibold">Manage Your Tags</p>}
        centered
        width={350}
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={() => setOpenModal(false)}
        okText={"Save"}
        okButtonProps={{ className: "bg-black text-white rounded-pill" }}
        cancelButtonProps={{
          className: "rounded-pill",
          style: { borderColor: "#E7040C ", color: "#E7040C" },
        }}
      >
        <div className="d-flex flex-column  gap-3">
          <span className="fs12px" style={{ color: "#7D7D7D" }}>
            Use <span className="text-black">tags</span> to group and find
            transactions across multiple categories. For example :
          </span>

          <ul className="ps-4 fs12px" style={{ color: "#7D7D7D" }}>
            <li>
              <span className="text-black">Reimburse</span> to track all your
              business expenses
            </li>

            <li>
              <span className="text-black">Spouse </span> to separate your
              spouse's transactions
            </li>
            <li>
              <span className="text-black">Graceland</span> for your summer
              vacation road trip
            </li>
          </ul>

          {addTag.map(({ m }, key) => (
            <div
              key={key}
              className="d-flex justify-content-between align-items-center p-1"
            >
              <Input
                className="p-3 w-75 fw-semibold"
                placeholder="Enter Tag "
                value={`Tags ${key + 1}`}
              />
              <div className="pe-3 ">
                <img src={deleteIcon} alt="trash" className=" cursor-pointer" />
              </div>
            </div>
          ))}

          <div
            className="p-3 rounded-3  d-flex gap-2 align-items-center justify-content-center cursor-pointer"
            style={{ backgroundColor: "#F9F9F9" }}
            onClick={() => setAddTag([...addTag, addTag])}
          >
            <img src={plusIcon} alt="add" />
            <span className="fs-6 fw-semibold" style={{ color: "#1bbb6b" }}>
              Add Tags
            </span>
          </div>
        </div>
      </Modal>

      <ManageMySpending
        navName={"Spending Report"}
        showQuickAction={true}
        pageClassName="dashboard-page report-theme-page spending-report-page"
        contentClassName="dashboard-content"
      >
        <div className="report-shell">
          <div className="report-overview">
            <div>
              <span className="dashboard-eyebrow">Spending insights</span>
              <h2>Spending Report</h2>
              <p>Review income, spending categories, filters, and account activity in a focused dashboard workspace.</p>
            </div>
          </div>

          <div className="row g-3 mt-4">
          <div className="report-toolbar d-flex justify-content-sm-between justify-content-center flex-wrap flex-sm-wrap align-items-center gap-2">
            <span className="fs-5 fw-semibold">Spending Report</span>
            <div className="d-flex gap-2 align-items-center">
              <Button2
                buttonName={"Manage Tags"}
                className={"btn-outline-success"}
                divClassName={"gap-1"}
                onClick={() => setOpenModal(true)}
                icon={<img src={settingIcon} alt="setting" />}
              />
            </div>
          </div>

          <div className="col-lg-8 mt-3 ">
            <div className="report-card d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center p-3 px-4 border-bottom">
                <span className="fs-5 fw-medium">Bank Accounts</span>
                <div className="d-flex align-items-center gap-2 justify-content-end">
                  <Select
                    className="rounded-pill"
                    defaultValue={`Last ${daysFilter} days`}
                    options={[
                      { name: "Last 30 days", value: 30 },
                      { name: "Last 60 days", value: 60 },
                      { name: "Last 90 days", value: 90 },
                    ]}
                    onChange={(e) => setDaysFilter(Number(e.target.value))}
                  />
                  <Select className="rounded-pill w-25" defaultValue="Type"
                    options={[
                      { name: "All", value: "all" },
                      { name: "Income", value: "in" },
                      { name: "Expenses", value: "out" },
                    ]}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  />
                  <span
                    className="fs14px fw-semibold cursor-pointer "
                    style={{ color: "#1bbb6b" }}
                    onClick={() => navigate("/budget-report")}
                  >
                    View all categories
                  </span>
                </div>
              </div>

              <div className="d-flex flex-column gap-3 p-3 px-4">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-md-0">
                  <div className="d-flex flex-column  ">
                    <span className="fs12px " style={{ color: "#7D7D7D" }}>
                      Total Income of last {daysFilter} days
                    </span>
                    <span
                      className="fs-5 fw-medium"
                      style={{ color: "#1bbb6b" }}
                    >
                      {fmt(totalIncome)}
                    </span>
                  </div>
                  <div style={{ width: "65%" }}>
                    <ProgressBar
                      percent={100}
                      strokeColor="#1bbb6b"
                      strokeWidth={"15px"}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 gap-md-0">
                  <div className="d-flex flex-column  ">
                    <span className="fs12px " style={{ color: "#7D7D7D" }}>
                      Total Spending of last {daysFilter} days
                    </span>
                    <span
                      className="fs-5 fw-medium"
                      style={{ color: "#FC555B" }}
                    >
                      -{fmt(totalSpending)}
                    </span>
                  </div>
                  <div style={{ width: "65%" }}>
                    <MultiColorBar border={true} colors={multiColorSegments} />
                  </div>
                </div>

                <Table
                  SpendingReportTable={true}
                  tableBody={spendingReportData}
                  tableHeaders={spendingReportHeaders}
                  condition1={"col-6"}
                  condition2={""}
                  keyNumber1={0}
                  className={"w-100"}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-4 mt-3">
            <div className="report-card d-flex flex-column justify-content-between h-100">
              <div className="d-flex flex-column ">
                <div className="d-flex justify-content-between align-items-center p-3">
                  <span className="fs-5 fw-semibold">Filter</span>
                  <span
                    className="fw-medium fs14px cursor-pointer"
                    style={{ color: "#1bbb6b" }}
                    onClick={() => setDaysFilter(30)}
                  >
                    Reset
                  </span>
                </div>
                {/* Date */}
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                  <span className="fw-medium">Date</span>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="fs14px" style={{ color: "#7D7D7D" }}>
                      Last {daysFilter} Days
                    </span>
                    <img
                      src={downIcon}
                      alt="down"
                      className="cursor-pointer"
                      width={18}
                      height={18}
                    />
                  </div>
                </div>

                {/* Category */}
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                  <span className="fw-medium">Categories</span>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="fs14px" style={{ color: "#7D7D7D" }}>
                      {categoryBreakdown.length} Found
                    </span>
                    <img
                      src={plusIcon}
                      alt="add"
                      className="cursor-pointer"
                      width={18}
                      height={18}
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="d-flex justify-content-between align-items-center border-bottom p-3">
                  <span className="fw-medium">Tags</span>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="fs14px" style={{ color: "#7D7D7D" }}>
                      0 Selected
                    </span>
                    <img
                      src={plusIcon}
                      alt="add"
                      className="cursor-pointer"
                      width={18}
                      height={18}
                    />
                  </div>
                </div>

                {/* Accounts */}
                <div className="d-flex flex-column gap-2 p-3 ">
                  <div className="d-flex justify-content-between align-items-center ">
                    <span className="fw-medium">Accounts</span>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="fs14px" style={{ color: "#7D7D7D" }}>
                        {accountsList.length} Selected
                      </span>
                      <img
                        src={plusIcon}
                        alt="add"
                        className="cursor-pointer"
                        width={18}
                        height={18}
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <span className="fs14px" style={{ color: "#7d7d7d" }}>
                      Bank Accounts
                    </span>
                    {accountsList.length === 0 ? (
                      <span className="fs12px" style={{ color: "#B0B0B0" }}>
                        No accounts found
                      </span>
                    ) : (
                      accountsList.map(({ name, icon }, key) => (
                        <div
                          key={key}
                          className="d-flex gap-2 align-items-center"
                        >
                          <Checkbox defaultChecked />
                          <img src={icon} width={20} height={20} alt="icon" />
                          <span className="fs12px fw-medium">{name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <Button1
                buttonName={"View Transaction"}
                className={"btn-outline-success m-3"}
                onClick={() => navigate("/my-transaction")}
              />
            </div>
          </div>
        </div>
        </div>
      </ManageMySpending>
    </>
  );
};

export default SpendingReport;
